'use client'

import { useRouter, useSearchParams } from 'next/navigation'
import { useEffect, useRef } from 'react'

import { useMaestroSession } from '@maestro/commerce-blocks/providers/maestro-session/maestro-session.context'
import { useEventBusContext } from '@maestro/core/event/event-bus.context'
import { type EventPayloadType, EventType } from '@maestro/data-layer'
import { theme } from '@maestro/local/theme'

export const useLoginTracking = () => {
  const { publish } = useEventBusContext()
  const { session } = useMaestroSession()
  const searchParams = useSearchParams()
  const router = useRouter()
  const hasTrackedLoginRef = useRef(false)
  const hasTrackedSignupRef = useRef(false)

  useEffect(() => {
    const loginSuccessToken = searchParams.get(
      theme().loginPage.successQueryParam
    )
    const signupSuccessToken = searchParams.get(
      theme().registerPage.successQueryParam
    )
    const loginMethod = searchParams.get('loginMethod') || 'email'
    const hashedEmail = session?.user?.hashedEmail ?? ''

    let shouldRemoveParams = false

    const user_id = session?.user?.id
    const user_data = {
      email_address: session?.user?.email,
      phone_number: session?.user?.phone,
      first_name: session?.user?.name || undefined,
      last_name: session?.user?.lastName,
      street: session?.user?.defaultAddress?.line1,
      city: session?.user?.defaultAddress?.town,
      region: undefined,
      postal_code: session?.user?.defaultAddress?.postalCode,
      country: session?.user?.defaultAddress?.country?.name,
      total_order_count: session?.user?.orderCount || 0,
      total_lifetime_value: session?.user?.orderTotals || 0,
    }

    if (loginSuccessToken && hashedEmail && !hasTrackedLoginRef.current) {
      shouldRemoveParams = true

      const tokenTimestamp = parseInt(loginSuccessToken, 36)
      const now = Date.now()
      const timeDiff = now - tokenTimestamp

      // Only fire event if token is valid (within last 15 seconds)
      if (!isNaN(tokenTimestamp) && timeDiff >= 0 && timeDiff < 15000) {
        const payload: EventPayloadType[EventType.USER_LOGGED_IN] = {
          hashedEmail,
          method: loginMethod as 'email' | 'vipps',
          user_id,
          user_data,
        }

        publish<EventPayloadType[EventType.USER_LOGGED_IN]>(
          EventType.USER_LOGGED_IN,
          payload
        )

        publish<EventPayloadType[EventType.USER_IDENTIFIED]>(
          EventType.USER_IDENTIFIED,
          { hashedEmail }
        )

        hasTrackedLoginRef.current = true
      }
    }

    if (signupSuccessToken && hashedEmail && !hasTrackedSignupRef.current) {
      shouldRemoveParams = true

      const tokenTimestamp = parseInt(signupSuccessToken, 36)
      const now = Date.now()
      const timeDiff = now - tokenTimestamp

      // Only fire event if token is valid (within last 15 seconds)
      if (!isNaN(tokenTimestamp) && timeDiff >= 0 && timeDiff < 15000) {
        const payload: EventPayloadType[EventType.USER_SIGNED_UP] = {
          hashedEmail,
          method: loginMethod as 'email' | 'vipps',
          user_id,
          user_data,
        }

        publish<EventPayloadType[EventType.USER_SIGNED_UP]>(
          EventType.USER_SIGNED_UP,
          payload
        )

        if (session?.user.isMember) {
          publish<EventPayloadType[EventType.IS_LOYALTY_CLUB_MEMBER]>(
            EventType.IS_LOYALTY_CLUB_MEMBER,
            {
              hashedEmail: hashedEmail ?? '',
              isClubMember: true,
            }
          )
          publish<EventPayloadType[EventType.JOINED_CLUB]>(
            EventType.JOINED_CLUB,
            {
              user_id,
              user_data,
            }
          )
        }

        hasTrackedSignupRef.current = true
      }
    }

    if (shouldRemoveParams) {
      const params = new URLSearchParams(searchParams.toString())
      params.delete(theme().loginPage.successQueryParam)
      params.delete(theme().registerPage.successQueryParam)
      params.delete('loginMethod')
      const newUrl = params.toString()
        ? `${window.location.pathname}?${params.toString()}`
        : window.location.pathname

      router.replace(newUrl, { scroll: false })
    }
  }, [searchParams, session, publish, router])
}
